// A reply in the Notes chat is an ordinary note that starts with a hidden link
// to the note it answers: "<!--reply:NOTEID-->text". HTML comments are left out
// wherever notes are shown (DOMPurify drops them), like the file marker
// "<!--file:ID-->" of files shared in the chat. No database change needed.

const REPLY_MARK = /<!--reply:([\w-]+)-->/;
const ANY_MARK = /<!--(?:file|reply):[\w-]+-->/g;
const FILE_MARKS = /<!--file:[\w-]+-->/g;

/** The note text to store for a reply to `replyToId`. */
export const replyNoteText = (replyToId: string, text: string) =>
  `<!--reply:${replyToId}-->${text}`;

/** The id of the note this note answers, or null when it isn't a reply. */
export const parseReplyNote = (text: string | null | undefined) =>
  text?.match(REPLY_MARK)?.[1] ?? null;

/** The note without its hidden reply / file markers. */
export const stripNoteMarkers = (text: string | null | undefined) =>
  (text ?? "").replace(ANY_MARK, "");

/** The text a person edits: markers hidden (they are put back on save). */
export const editableNoteText = (text: string | null | undefined) =>
  stripNoteMarkers(text).trim();

/** Puts the original note's markers back around edited text. */
export const keepNoteMarkers = (original: string, edited: string) => {
  const reply = original.match(REPLY_MARK)?.[0] ?? "";
  const files = (original.match(FILE_MARKS) ?? []).join("");
  return `${reply}${stripNoteMarkers(edited)}${files}`;
};

/** A short plain-text preview of a note for the reply quote. */
export const noteSnippet = (text: string | null | undefined, max = 80) => {
  const plain = stripNoteMarkers(text)
    .replace(/@\[(.*?)\]/g, "@$1")
    .replace(/<\s*br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li)>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length <= max) return plain;
  const cut = plain.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const shown = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${shown.trimEnd()}…`;
};

/**
 * Adds an @mention of the person being replied to at the start of the text,
 * unless they were already mentioned (typed or picked from the list).
 */
export const withReplyMention = (
  text: string,
  name: string,
  alreadyMentioned: boolean,
) => {
  const display = name.trim();
  if (!display || alreadyMentioned || text.includes(`@[${display}]`)) {
    return text;
  }
  return `@[${display}] ${text}`;
};
